<template>
	<section class="scene-onboarding" disabled:drag="Handle_OnDrag" v-pointer:up="Handle_OnPointerUp">
		<ChalkyUtilityDots v-bind:activeIndex="currentStep" v-bind:amount="5" />

		<section class="slides">
			<ViewOnboardingWelcome
				v-bind:class="{ 'is-active': currentStep === 0 }"
				v-bind:style="getStyle(0)"
				v-on:continue="nextStepOrComplete"
			/>
			<ViewOnboardingLocation
				v-bind:class="{ 'is-active': currentStep === 1 }"
				v-bind:style="getStyle(1)"
				v-on:continue="nextStepOrComplete"
			/>
			<ViewOnboardingTv
				v-bind:class="{ 'is-active': currentStep === 2 }"
				v-bind:style="getStyle(2)"
				v-on:continue="nextStepOrComplete"
			/>
			<ViewOnboardingUnlock
				v-bind:class="{ 'is-active': currentStep === 3 }"
				v-bind:style="getStyle(3)"
				v-on:continue="nextStepOrComplete"
			/>
			<ViewOnboardingJoin
				v-bind:class="{ 'is-active': currentStep === 4 }"
				v-bind:style="getStyle(4)"
				v-on:continue="nextStepOrComplete"
				v-on:signup="Handle_OnClickSignup"
			/>
		</section>
	</section>
</template>

<script lang="ts">
	import ChalkySticks from '@chalkysticks/sdk';
	import Router from '@/Router';
	import SceneBase from '@/Core/Scene.vue';
	import gsap from 'gsap';
	import { Component, Prop } from 'vue-property-decorator';
	import { beforeDestroy, mounted } from '@/Utility/Decorators';

	/**
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package Scene
	 * @project ChalkySticks App
	 */
	@Component
	export default class SceneOnboarding extends SceneBase {
		/**
		 * Current onboarding step
		 *
		 * @type number
		 */
		protected currentStep: number = 0;

		/**
		 * Current drag offset in pixels
		 *
		 * @type number
		 */
		protected dragRatio: number = 0;

		/**
		 * Whether we're currently dragging
		 *
		 * @type boolean
		 */
		protected isDragging: boolean = false;

		/**
		 * Maximum number of steps in the onboarding process
		 *
		 * @type number
		 */
		protected maxSteps: number = 5;

		/**
		 * @return void
		 */
		protected completeOnboarding(): void {
			this.$store.dispatch('settings/completeOnboarding');
			ChalkySticks.Core.Event.Bus.dispatch('onboarding:completed');
			Router.replace({ name: 'PageHome' });
		}

		/**
		 * Go to next step or complete onboarding
		 *
		 * @return void
		 */
		protected nextStepOrComplete(): void {
			if (this.currentStep < this.maxSteps) {
				this.animateDirection(1);
			} else {
				this.Handle_OnCompleteOnboarding();
			}
		}

		/**
		 * Lifecycle hook called when the component is mounted
		 *
		 * @return Promise<void>
		 */
		protected async animateDirection(direction: number): Promise<void> {
			const properties = {
				dragRatio: direction * -1,
				duration: 0.3,
				ease: 'power2.out',
			};

			await gsap.to(this, properties);

			this.currentStep += direction;
			this.dragRatio = 0;

			// Check if we're done
			if (this.currentStep === this.maxSteps) {
				this.Handle_OnCompleteOnboarding();
			}
		}

		/**
		 * Returns style object for a step in the carousel.
		 * Only current and adjacent slides are visible and animated.
		 *
		 * @param number index
		 * @return object
		 */
		protected getStyle(index: number): object {
			const amount = 20;
			const startIndex = index - this.currentStep;
			let opacity = 0;
			let transform = `translateX(${startIndex * amount}%)`;
			let zIndex = 1;

			// Active slide
			if (startIndex === 0) {
				opacity = 1 - Math.abs(this.dragRatio);
				transform = `translateX(${this.dragRatio * amount}%)`;
				zIndex = 2;
			}

			// Adjacent slide
			else if ((startIndex === 1 && this.dragRatio < 0) || (startIndex === -1 && this.dragRatio > 0)) {
				const direction = startIndex === 1 ? 1 : -1;
				opacity = Math.abs(this.dragRatio);
				transform = `translateX(${direction * amount + this.dragRatio * amount}%)`;
				zIndex = 3;
			}

			return {
				opacity,
				transform,
				zIndex,
			};
		}

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * Complete onboarding and redirect to home page
		 *
		 * @return Promise<void>
		 */
		protected async Handle_OnClickSignup(): Promise<void> {
			this.$store.dispatch('settings/completeOnboarding');

			this.$router.push({
				name: 'PageSignup',
			});
		}

		/**
		 * Complete onboarding and redirect to home page
		 *
		 * @return Promise<void>
		 */
		protected async Handle_OnCompleteOnboarding(): Promise<void> {
			this.completeOnboarding();
		}

		/**
		 * Handle drag events
		 *
		 * @param any event
		 * @return Promise<void>
		 */
		protected async Handle_OnDrag(event: any): Promise<void> {
			this.isDragging = true;
			this.dragRatio = ChalkySticks.Utility.Math.clamp(event.data.dx / 200, -1, 1);

			if (this.currentStep === 0 && this.dragRatio > 0) {
				this.dragRatio = 0;
			}
		}

		/**
		 * Handle pointer up to complete drag gesture
		 *
		 * @return Promise<void>
		 */
		protected async Handle_OnPointerUp(): Promise<void> {
			let direction = this.dragRatio > 0 ? -1 : 1;

			if (this.dragRatio < -0.5 || (this.dragRatio > 0.5 && this.currentStep > 0)) {
				this.animateDirection(direction);
			} else {
				this.animateDirection(0);
			}
		}

		// endregion: Event Handlers
	}
</script>

<style lang="scss">
	.scene-onboarding {
		cursor: default;
		height: 100%;
		overflow: hidden;
		position: relative;
		width: 100%;

		* {
			-webkit-user-select: none;
			-moz-user-select: none;
			-ms-user-select: none;
			user-select: none;
		}

		&::before {
			background: url('~@chalkysticks/sass/build/asset/image/icon/chalkysticks.svg') no-repeat center center;
			background-size: 100%;
			content: ' ';
			height: 30svw;
			left: 50%;
			opacity: 0.2;
			position: absolute;
			top: 50%;
			transform: translate(-50%, -50%);
			width: 30svw;
			z-index: 1;
		}

		.slides {
			height: 100%;
			position: relative;
			z-index: 2;
		}

		.utility-dots {
			bottom: 200px;
			display: none;
			left: 50%;
			position: absolute;
			transform: translateX(-50%);
			z-index: 10;
		}

		[class*='view-onboarding-'] {
			--dot-size: 9px;

			align-items: center;
			background-position: center;
			background-repeat: no-repeat;
			background-size: cover;
			display: grid;
			flex-shrink: 0;
			grid-template-columns: 1fr;
			grid-template-rows: 65% auto 15%;
			height: 100%;
			justify-content: center;
			left: 0;
			position: absolute;
			top: 0;
			width: 100%;
		}

		.graphic {
			align-self: flex-end;
			margin: 0 auto;
			max-width: 350px;
			padding: 1rem;
			text-align: center;

			video,
			img {
				border-radius: 20px;
				height: 100%;
				object-fit: cover;
				width: 100%;
			}

			.utility-dots {
				margin: 0.5rem 0;
			}
		}

		.content {
			padding: 1rem;
			text-align: center;
			transform: scale(0.9);
			transform-origin: center top;

			.description {
				color: var(--chalky-grey);
			}
		}

		.actions {
			align-self: flex-start;
			display: inline-flex;
			gap: 0.5rem;
			justify-content: center;
			padding: 0 2rem;
			text-align: center;

			button {
				width: 100%;
			}
		}
	}
</style>
